import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, IconButton, Typography, Avatar } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { DataTable } from "../../libs/core/components/data-table";
import { Gallery } from "./types/gallery";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditGallery from "./components/AddEditGallery";
import { useDeleteGalleryMutation, useGetGalleriesQuery } from "./api/galleryApi";

const GalleryManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);

  // Using RTK Query to fetch galleries
  const { data: galleries, isLoading, isError, isFetching } = useGetGalleriesQuery();
  const [deleteGallery] = useDeleteGalleryMutation();

  const columns: Array<MRT_ColumnDef<Gallery>> = [
    {
      header: "Image",
      accessorKey: "image",
      size: 100,
      Cell({ row: { original } }) {
        return (
          <Avatar
            src={original.image}
            alt={original.title}
            sx={{ width: 60, height: 60 }}
            variant="rounded"
          />
        );
      },
    },
    {
      header: "Title",
      accessorKey: "title",
      size: 200,
    },
    {
      header: "Description",
      accessorKey: "description",
      size: 300,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {original.description}
          </Typography>
        );
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      size: 150,
      Cell({ row: { original } }) {
        return <span>{new Date(original.created_at).toLocaleDateString()}</span>;
      },
    },
  ];

  return (
    <Box>
      <DataTable<Gallery>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Gallery</Typography>
              <Typography
                color="primary"
                variant="body2"
                sx={{ 
                  px: 2, 
                  py: 0.5, 
                  border: 1, 
                  borderColor: 'primary.main', 
                  borderRadius: 1,
                  fontSize: '0.875rem'
                }}
              >
                {galleries?.data?.length ?? 0} Photos
              </Typography>
            </Box>
            <Button
              onClick={() => {
                setSelectedGallery(null);
                setOpenDialog(true);
              }}
              sx={{ minWidth: "200px" }}
              variant="contained"
            >
              Add New Photo
            </Button>
          </Box>
        }
        list={galleries?.data ?? []}
        count={galleries?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        renderRowActions={(row) => (
          <Box display={"flex"} gap={1}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedGallery(row);
                setOpenConfirmModal(true);
              }}
            >
              <Delete />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedGallery(row);
                setOpenDialog(true);
              }}
            >
              <Edit />
            </IconButton>
          </Box>
        )}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowNumberMode={undefined}
      />

      {/* Add/Edit Gallery Dialog */}
      <AddEditGallery 
        open={openDialog} 
        gallery={selectedGallery}
        setModalOpen={setOpenDialog} 
      />

      {/* Confirm Delete Modal */}
      {selectedGallery && (
        <ConfirmModal
          type="primary"
          title="Delete Gallery Item"
          message={`Are you sure you want to delete '${selectedGallery.title}'?`}
          modalOpen={openConfirmModal}
          setModalOpen={setOpenConfirmModal}
          onOk={() => {
            deleteGallery(selectedGallery.id.toString());
            setOpenConfirmModal(false);
            setSelectedGallery(null);
          }}
        />
      )}
    </Box>
  );
};

export default GalleryManager; 